/**
 * Favor math calculator (sections/fishers-favor-band.liquid).
 *
 * Progressive enhancement over the static tier table: reveals a guest-count
 * input and recomputes cases / total / per-guest cost from the same price
 * data the section rendered server-side (data attributes, prices in cents).
 * Without JS the element stays `hidden` and the static table stands alone.
 * Deliberately import-free so the band never waits on the theme module
 * graph.
 */
class FavorCalculator extends HTMLElement {
  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  connectedCallback() {
    const input = this.querySelector('input');
    const result = this.querySelector('[data-result]');
    if (!input || !result) return;

    try {
      this.#formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
    } catch {
      this.#formatter = null;
    }

    input.addEventListener('input', () => this.#update(input, result));
    this.#update(input, result);
    this.hidden = false;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {Element} result
   */
  #update(input, result) {
    const caseSize = Number(this.dataset.caseSize);
    const casePriceCents = Number(this.dataset.casePrice);
    const guests = Math.floor(Number(input.value));

    if (!Number.isFinite(guests) || guests < 1 || !(caseSize >= 1) || !(casePriceCents > 0)) {
      result.textContent = '';
      return;
    }

    const cases = Math.ceil(guests / caseSize);
    const totalCents = cases * casePriceCents;
    const spares = cases * caseSize - guests;

    const parts = [
      `${cases} ${cases === 1 ? 'case' : 'cases'}`,
      `${this.#money(totalCents)} total`,
      `≈ ${this.#money(totalCents / guests)} per guest`,
    ];
    if (spares > 0) {
      parts.push(`${spares} spare ${spares === 1 ? 'bag' : 'bags'}`);
    }

    result.textContent = parts.join(' · ');
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const amount = cents / 100;
    if (this.#formatter) return this.#formatter.format(amount);
    return `$${amount.toFixed(2)}`;
  }
}

if (!customElements.get('favor-calculator')) {
  customElements.define('favor-calculator', FavorCalculator);
}
